import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import './OrdersPage.css';
import Footer from '../components/Footer'; // ✅ Footer imported
import { generateTicketPDF } from '../generateTicket'; // ✅ Import ticket PDF generator
import Papa from 'papaparse';
import { useNavigate } from 'react-router-dom';
import { FaEdit, FaCheck } from 'react-icons/fa';

const formatFirebaseTimestamp = (timestamp) => {
  // If it's already a string, return it as-is
  if (typeof timestamp === 'string') return timestamp;

  // If it's a Firestore timestamp object
  if (timestamp && typeof timestamp === 'object') {
    // New Firestore SDK format (v9+)
    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    }
    // Legacy Firestore timestamp format
    else if (timestamp.seconds) {
      const date = new Date(timestamp.seconds * 1000);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    }
  }

  // If it's a JavaScript Date object
  if (timestamp instanceof Date) {
    return timestamp.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  }

  // If we can't format it, return the raw value (instead of 'N/A')
  return JSON.stringify(timestamp);
};

const blobToBase64 = (blob) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onloadend = () => resolve(reader.result.split(',')[1]); // Extract Base64
  });
};

const fetchEventLocations = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'events'));
    const locations = {};

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      locations[data.name] = {
        location: data.location || 'Venue TBD',
        datetime: data.datetime || 'Date & Time TBD',
      };
    });

    return locations; // ✅ Return correct event locations
  } catch (error) {
    console.error('Error fetching event locations:', error);
    return {};
  }
};

const resendEmail = async (order) => {
  try {
    console.log(`Resending email to ${order.email}...`);

    // ✅ Fetch event locations before generating emails
    const eventLocationsData = await fetchEventLocations();

    // ✅ Generate PDFs for each ticket
    const ticketPDFs = await Promise.all(
      order.tickets.map(async (ticket, index) => {
        // ✅ Retrieve correct event location and datetime
        const eventLocation =
          eventLocationsData[ticket.eventName]?.location || 'Venue TBD';
        const datetime =
          eventLocationsData[ticket.eventName]?.datetime || 'Date & Time TBD';

        const ticketDetails = {
          orderNumber: order.orderNumber,
          customerName: order.name,
          eventName: ticket.eventName,
          eventLocation, // ✅ Correctly assigned
          datetime, // ✅ Correctly assigned
          ticketNumber:
            ticket.ticketNumber ?? `${order.orderNumber}-${index + 1}`, // ✅ Provide fallback
          ticketType: ticket.ticketType,
        };

        console.log('Generated Ticket Details:', ticketDetails); // ✅ Debugging Log

        const pdfBlob = await generateTicketPDF(ticketDetails);
        const pdfBase64 = await blobToBase64(pdfBlob);

        return {
          filename: `BCT_${ticketDetails.ticketNumber}_RESEND.pdf`,
          content: pdfBase64,
          encoding: 'base64',
        };
      })
    );

    // ✅ Send email request to the Cloud Function
    const response = await fetch(
      'https://us-central1-black-canyon-tickets-bct.cloudfunctions.net/sendOrderConfirmation',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: order.name,
          email: order.email,
          orderNumber: order.orderNumber,
          total: order.total,
          phone: order.phone,
          address: order.address,
          tickets: order.tickets.map((ticket, index) => ({
            eventName: ticket.eventName,
            eventLocation:
              eventLocationsData[ticket.eventName]?.location || 'Venue TBD',
            datetime:
              eventLocationsData[ticket.eventName]?.datetime ||
              'Date & Time TBD',
            ticketNumber:
              ticket.ticketNumber ?? `${order.orderNumber}-${index + 1}`, // ✅ Provide fallback
            ticketType: ticket.ticketType,
          })),
          attachments: ticketPDFs, // ✅ Attach generated PDFs
        }),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to resend email');
    }

    alert(`Order confirmation email resent to ${order.email}`);
  } catch (error) {
    console.error('Error resending email:', error);
    alert('Failed to resend email. Try again.');
  }
};

function OrdersPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState('All Events');
  const [editableEmails, setEditableEmails] = useState({}); // Tracks editable email values
  const [isEditing, setIsEditing] = useState({}); // Tracks which email is being edited
  const [isSaving, setIsSaving] = useState(false); // Tracks saving status

  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [selectedRefundOrder, setSelectedRefundOrder] = useState(null);
  const [selectedRefundTicket, setSelectedRefundTicket] = useState('full');
  const [customRefundAmount, setCustomRefundAmount] = useState('');

  const [refunds, setRefunds] = useState([]);
  const [refundDetailsForOrder, setRefundDetailsForOrder] = useState(null);

  // Enable editing for a specific order's email
  const enableEditing = (orderId) => {
    setIsEditing((prev) => ({
      ...prev,
      [orderId]: true,
    }));
    setEditableEmails((prev) => ({
      ...prev,
      [orderId]: orders.find((order) => order.id === orderId).email,
    }));
  };

  // Save the updated email to Firestore
  const saveEmail = async (orderId) => {
    const newEmail = editableEmails[orderId];
    if (!newEmail || !newEmail.includes('@')) {
      alert('Please enter a valid email address.');
      return;
    }

    setIsSaving(true);
    try {
      // Update the email in Firestore
      await updateDoc(doc(db, 'orders', orderId), { email: newEmail });

      // Update the local state to reflect the change
      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order.id === orderId ? { ...order, email: newEmail } : order
        )
      );

      // Disable editing mode for this order
      setIsEditing((prev) => ({
        ...prev,
        [orderId]: false,
      }));

      alert('Email updated successfully!');
    } catch (error) {
      console.error('Error updating email:', error);
      alert('Failed to update email. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle email input changes
  const handleEmailChange = (orderId, newEmail) => {
    setEditableEmails((prev) => ({
      ...prev,
      [orderId]: newEmail,
    }));
  };

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'orders'));
        const orderList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setOrders(orderList);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching orders:', error);
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  const refreshRefunds = async () => {
  const snapshot = await getDocs(collection(db, 'refunds'));
  const refundList = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
  setRefunds(refundList);
};

useEffect(() => {
  refreshRefunds();
}, []);

  const openVisualization = () => {
    navigate('/admin/orders-visuals', { state: { orders } });
  };

  const getRefundsForOrder = (orderId) => {
    return refunds.filter((r) => r.orderId === orderId);
  };

  const eventNames = [
    ...new Set(
      orders
        .flatMap((order) =>
          Array.isArray(order.tickets)
            ? order.tickets.map((ticket) => ticket.eventName)
            : []
        )
        .filter(Boolean)
    ),
  ];

  if (loading) {
    return <div className="loading">Loading orders...</div>;
  }

 const filteredOrders = orders
  .filter(
    (order) =>
      selectedEvent === 'All Events' ||
      (Array.isArray(order.tickets) &&
        order.tickets.some((ticket) => ticket.eventName === selectedEvent))
  )
  .sort((a, b) => {
    const dateA = a.createdAt?.seconds ?? 0;
    const dateB = b.createdAt?.seconds ?? 0;
    return dateB - dateA;
  });

  const totalTickets = filteredOrders.reduce((total, order) => {
    return (
      total +
      (Array.isArray(order.tickets)
        ? order.tickets.reduce(
            (sum, ticket) =>
              sum + (!ticket.refunded ? ticket.quantity ?? 1 : 0),
            0
          )
        : 0)
    );
  }, 0);

  const totalRefundedAmount = refunds
    .filter((r) => filteredOrders.some((o) => o.id === r.orderId))
    .reduce((sum, r) => sum + (r.refundedAmount || 0), 0);

  const totalRefundedTickets = filteredOrders.reduce((count, order) => {
    if (!Array.isArray(order.tickets)) return count;
    return (
      count + order.tickets.filter((ticket) => ticket.refunded === true).length
    );
  }, 0);

  const totalAmount = filteredOrders.reduce(
    (sum, order) => sum + (order.total || 0),
    0
  );

  const exportOrdersToCSV = () => {
    // Create an array where each ticket gets its own row
    const csvData = filteredOrders.flatMap((order) => {
      const baseOrderInfo = {
        OrderNumber: order.orderNumber,
        Name: order.name,
        Email: order.email,
        Phone: order.phone || 'N/A',
        Address: `${order.address?.line1 || ''}, ${
          order.address?.city || ''
        }, ${order.address?.state || ''}, ${
          order.address?.postal_code || ''
        }, ${order.address?.country || ''}`,
        OrderDate: formatFirebaseTimestamp(order.createdAt),
        OrderTotal: order.total,
      };

      if (!Array.isArray(order.tickets) || order.tickets.length === 0) {
        return [
          {
            ...baseOrderInfo,
            EventName: 'No tickets',
            TicketType: '',
            Quantity: '',
            TicketNumber: '',
            TicketPrice: '',
          },
        ];
      }

      return order.tickets.map((ticket, index) => ({
        ...baseOrderInfo,
        EventName: ticket.eventName || 'N/A',
        TicketType: ticket.ticketType || 'N/A',
        Quantity: ticket.quantity || 1,
        TicketNumber:
          ticket.ticketNumber || `${order.orderNumber}-${index + 1}`,
        TicketPrice: (ticket.ticketPrice || ticket.price || 0).toFixed(2),
      }));
    });

    // Convert JSON data to CSV format
    const csv = Papa.unparse(csvData, {
      header: true,
      columns: [
        'OrderNumber',
        'Name',
        'Email',
        'Phone',
        'Address',
        'OrderDate',
        'OrderTotal',
        'EventName',
        'TicketType',
        'Quantity',
        'TicketNumber',
        'TicketPrice',
      ],
    });

    // Create a downloadable CSV file
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'Detailed_Orders_Report.csv';
    link.click();
  };

  return (
    <div className="orders-container">
      <h1 className="orders-heading">Orders List</h1>

      <div className="filter-container">
        <div className="order-count">
          Total Orders: {filteredOrders.length}
          <div className="total-amount">
            Total Amount: ${totalAmount.toFixed(2)}
          </div>
          <div className="total-tickets">Total Tickets: {totalTickets}</div>
          <div className="total-refunded-amount">
            Total Refunded: ${totalRefundedAmount.toFixed(2)}
          </div>
          <div className="total-refunded-tickets">
            Tickets Refunded: {totalRefundedTickets}
          </div>
        </div>

        <select
          className="event-filter"
          value={selectedEvent}
          onChange={(e) => setSelectedEvent(e.target.value)}
        >
          <option value="All Events">All Events</option>
          {eventNames.map((eventName, index) => (
            <option key={index} value={eventName}>
              {eventName}
            </option>
          ))}
        </select>
        <button className="download-csv-btn" onClick={exportOrdersToCSV}>
          Download CSV
        </button>
        <button className="visualize-btn" onClick={openVisualization}>
          View Analytics
        </button>
      </div>

      <div className="orders-table-container">
        <table className="orders-table">
          <thead>
            <tr>
              <th>Order Number</th>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Address</th>
              <th>Total ($)</th>
              <th>Tickets</th>
              <th>Order Date</th>
              <th>Actions</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.map((order) => (
                <tr key={order.id}>
                  <td>{order.orderNumber}</td>
                  <td>{order.name}</td>
                  <td>
                    {isEditing[order.id] ? (
                      <div className="editable-email-container">
                        <input
                          type="email"
                          value={editableEmails[order.id] || order.email}
                          onChange={(e) =>
                            handleEmailChange(order.id, e.target.value)
                          }
                          className="editable-email"
                          disabled={isSaving}
                        />
                        <button
                          className="save-email-btn"
                          onClick={() => saveEmail(order.id)}
                          disabled={isSaving}
                        >
                          <FaCheck /> {/* Checkmark icon */}
                        </button>
                      </div>
                    ) : (
                      <div className="email-display-container">
                        <span>{order.email}</span>
                        <button
                          className="edit-email-btn"
                          onClick={() => enableEditing(order.id)}
                          disabled={isSaving}
                        >
                          <FaEdit /> {/* Pencil icon */}
                        </button>
                      </div>
                    )}
                  </td>
                  <td>{order.phone || 'N/A'}</td>
                  <td>
                    {order.address?.line1}, {order.address?.city},{' '}
                    {order.address?.state}, {order.address?.postal_code},{' '}
                    {order.address?.country}
                  </td>
                  <td>${Number(order.total).toFixed(2)}</td>
                  <td>
                    {Array.isArray(order.tickets) &&
                    order.tickets.length > 0 ? (
                      <ul className="ticket-list">
                        {order.tickets.map((ticket, index) => (
                          <li
                            key={index}
                            style={{
                              textDecoration: ticket.refunded
                                ? 'line-through'
                                : 'none',
                              color: ticket.refunded ? 'gray' : 'inherit',
                            }}
                          >
                            {ticket.eventName} ({ticket.ticketType}) - x
                            {ticket.quantity ?? 1} [
                            {ticket.ticketNumber ||
                              `${order.orderNumber}-${index + 1}`}
                            ]
                          </li>
                        ))}
                      </ul>
                    ) : (
                      'No tickets'
                    )}
                  </td>

                  <td>{formatFirebaseTimestamp(order.createdAt)}</td>
                  <td className="action-buttons">
                    <button
                      className="resend-email-btn"
                      onClick={() => resendEmail(order)}
                    >
                      Resend Email
                    </button>

                    {order.paymentIntentId ? (
                      <button
                        className="refund-btn"
                        onClick={() => {
                          setSelectedRefundOrder(order);
                          setRefundModalOpen(true);
                          setCustomRefundAmount(order.total); // Default to full amount
                          setSelectedRefundTicket('full');
                        }}
                        style={{ marginTop: '8px' }}
                      >
                        Refund
                      </button>
                    ) : (
                      <button
                        className="refund-btn"
                        disabled
                        style={{
                          marginTop: '8px',
                          backgroundColor: '#ccc',
                          color: '#666',
                          cursor: 'not-allowed',
                        }}
                      >
                        Refund (Unavailable)
                      </button>
                    )}
                  </td>
                  <td>
                    {getRefundsForOrder(order.id).length > 0 ? (
                      <button
                        className="refunded-flag-btn"
                        onClick={() => setRefundDetailsForOrder(order.id)}
                      >
                        REFUNDED
                      </button>
                    ) : (
                      '-'
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>

        {refundModalOpen && selectedRefundOrder && (
          <div className="refund-modal">
            <div className="refund-modal-content">
              <h3>Refund Order #{selectedRefundOrder.orderNumber}</h3>

              <label>Select Refund Option:</label>
              <select
                value={selectedRefundTicket}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedRefundTicket(value);

                  if (value === 'custom') {
                    setCustomRefundAmount(''); // let admin type it
                  } else if (value !== 'full') {
                    const index = parseInt(value);
                    const ticket = selectedRefundOrder.tickets?.[index];
                    const price = ticket?.ticketPrice || ticket?.price || 0;
                    setCustomRefundAmount(price); // autofill for ticket
                  } else {
                    setCustomRefundAmount(selectedRefundOrder.total); // full refund
                  }
                }}
              >
                <option value="full">Full Refund</option>
                <option value="custom">Custom Refund</option>
                {selectedRefundOrder.tickets?.map((ticket, index) => (
                  <option key={index} value={index}>
                    {ticket.ticketNumber ||
                      `${selectedRefundOrder.orderNumber}-${index + 1}`}{' '}
                    – {ticket.eventName} ({ticket.ticketType})
                  </option>
                ))}
              </select>

              {selectedRefundTicket === 'custom' && (
                <div>
                  <label>
                    Enter Refund Amount:
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max={selectedRefundOrder.total}
                      value={customRefundAmount}
                      onChange={(e) => setCustomRefundAmount(e.target.value)}
                    />
                  </label>
                </div>
              )}

              <p
                style={{
                  marginTop: '10px',
                  fontWeight: 'bold',
                  color: 'black',
                }}
              >
                Total refund being sent: $
                {Number(customRefundAmount || 0).toFixed(2)}
              </p>

              <div className="refund-modal-actions">
                <button
                  onClick={async () => {
                    const confirmed = window.confirm(
                      `Are you sure you want to refund ${
                        selectedRefundTicket === 'full'
                          ? 'the full order'
                          : `Ticket #${parseInt(selectedRefundTicket) + 1}`
                      } for $${customRefundAmount || 'full amount'}?`
                    );

                    if (!confirmed) return;

                    try {
                      const response = await fetch(
                        'https://us-central1-black-canyon-tickets-bct.cloudfunctions.net/processRefund',
                        {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            paymentIntentId:
                              selectedRefundOrder.paymentIntentId,
                            amount: parseFloat(customRefundAmount),
                            orderId: selectedRefundOrder.id,
                            ticketIndex:
                              selectedRefundTicket === 'full'
                                ? null
                                : Number(selectedRefundTicket),
                          }),
                        }
                      );

                      if (!response.ok) throw new Error('Refund failed');

                      const result = await response.json();
                      console.log('Refund success:', result);
                      await refreshRefunds(); // ✅ refresh refund data
                      alert('Refund processed successfully.');
                      setRefundModalOpen(false);
                      setSelectedRefundOrder(null);
                      setCustomRefundAmount('');
                      setSelectedRefundTicket('full');
                    } catch (err) {
                      console.error('Refund error:', err);
                      alert('Refund failed. Check console for details.');
                    }
                  }}
                >
                  Confirm Refund
                </button>
                <button
                  onClick={() => {
                    setRefundModalOpen(false);
                    setSelectedRefundOrder(null);
                    setCustomRefundAmount('');
                    setSelectedRefundTicket('full');
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {refundDetailsForOrder && (
        <div className="refund-details-modal">
          <div className="refund-details-content">
            <h3>Refund Details</h3>
            <ul>
              {getRefundsForOrder(refundDetailsForOrder).map((r, i) => (
                <li key={i}>
                  <strong>Amount:</strong> ${r.refundedAmount?.toFixed(2)}{' '}
                  <br />
                  <strong>Ticket:</strong>{' '}
                  {r.ticketName && r.ticketNumber
                    ? `${r.ticketName} – ${r.ticketNumber}`
                    : 'Full Order'}
                  <br />
                  <strong>Date:</strong>{' '}
                  {new Date(
                    r.timestamp?.seconds
                      ? r.timestamp.seconds * 1000
                      : r.timestamp
                  ).toLocaleString()}
                </li>
              ))}
            </ul>
            <button onClick={() => setRefundDetailsForOrder(null)}>
              Close
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

export default OrdersPage;
